<template>
  <div class="payment">

    <!-- Header -->
    <div class="payment-line">
      <h1 class="payment-line-title"> {{ $t('My payment details') }} </h1>
    </div>
    <!---->

    <!-- Location cards -->
    <section class="payment-block">

      <!-- If user hasn't tel number -->
      <template v-if="!user.telephone">
        <div class="payment-block-telephone">
          <h3 class="payment-block-telephone-title"> {{ $t('Please provide your phone number') }} </h3>
          <div class="payment-block-telephone-wrapper">
            <base-input class="mb10 input payment-block-telephone-field"
                        type="text"
                        name="phone-number"
                        :mask="{
                        mask: '+38(999)-999-9999',
                        showMaskOnHover: false,
                        autoUnmask: true
                      }"
                        v-model.trim="telephone"
                        @blur="$v.telephone.$touch()"
                        :placeholder="$t('Phone Number *')"
                        autocomplete="tel"
                        :validations="[
                        {
                          condition: $v.telephone.$error && !$v.telephone.required,
                          text: $t('Field is required.')
                        },
                        {
                          condition: $v.telephone.$error && !$v.telephone.minLength,
                          text: $t('Incorrect type of number')
                        },
                        {
                          condition: $v.telephone.$error && !$v.telephone.incorrectOperatorCodeWithTwo0 && $v.telephone.required,
                          text: $t('Operator code cannot start with 00')
                        },
                        {
                          condition: $v.telephone.$error && !$v.telephone.incorrectOperatorCode && $v.telephone.required,
                          text: $t('Operator code must start with 0')
                        }
                      ]"
            />
            <button-full class="payment-block-telephone-btn"
                         :disabled="$v.$invalid"
                         @click.native="sendTelephoneValue"
            >
              {{ $t('Save') }}
            </button-full>
          </div>
        </div>
      </template>
      <!---->

      <template v-else-if="!ipayLoading">
        <!-- Add new card button -->
        <div class="payment-block-actions">
          <button-full class="payment-block-actions-btn" @click.native="openAddCardLink">
            {{ isUserInvited ? $t('Connect iPay') : $t('Add another card') }}
          </button-full>
        </div>
        <!---->

        <!-- Cards -->
        <div class="payment-block-list" v-if="isUserExists && getCards.length">
          <credit-card class="payment-block-list-item"
                       v-for="card in getCards"
                       :card="card"
                       :key="card.details.uid"
          />
        </div>
        <!---->

        <!-- Nothing there -->
        <div class="payment-block-nothing" v-if="noCardsMessage">
          <span class="payment-block-nothing-text"> {{ $t(noCardsMessage) }} </span>
        </div>
        <!---->
      </template>
      <template v-else>
        <bounce-loader />
      </template>
    </section>
    <!---->

  </div>
</template>

<script async>
import { required, minLength } from 'vuelidate/lib/validators'
import { mapGetters, mapActions } from 'vuex'

const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput');
const BounceLoader = () => import(/* webpackChunkName: "vsf-bounce-loader" */ 'theme/components/theme/blocks/Home/Preloaders/BounceLoader');
const CreditCard = () => import(/* webpackChunkName: "vsf-credit-card" */ '../../../../../../modules/ipay-payment/components/CreditCard');
const ButtonFull = () => import(/* webpackChunkName: "vsf-button-full" */ 'theme/components/theme/ButtonFull');

const incorrectOperatorCode = (value, vm) => (value.charAt(0) === '0');
const incorrectOperatorCodeWithTwo0 = (value, vm) => (value.charAt(1) !== '0');

export default {
  name: 'MyPaymentDetails',
  components: {
    ButtonFull,
    BaseInput,
    CreditCard,
    BounceLoader
  },
  data () {
    return {
      user: {},
      telephone: '',
      ipayLoading: false
    }
  },
  mounted () {
    this.user = Object.assign({}, this.$store.state.user.current)
  },
  async created () {
    await this.$bus.$on('update-telephone-value', () => {
      this.user.telephone = this.telephone
      this.getPaymentData()
    })
    if (this.user.telephone) await this.getPaymentData()
  },
  validations: {
    telephone: {
      required,
      minLength: minLength(10),
      incorrectOperatorCode,
      incorrectOperatorCodeWithTwo0
    }
  },
  computed: {
    ...mapGetters({
      isUserExists: 'ipay-payment-user/isUserExist',
      getCards: 'ipay-payment-user/getCards',
      isUserInvited: 'ipay-payment-user/isUserInvited',
      getPhoneNumber: 'user/getPhoneNumber'
    }),
    getCustomerId () {
      return this.$store.state.user.current.id
    },
    getCustomerToken () {
      return this.$store.state.user.token
    },
    successUrl () {
      return window ? window.location.href : '/my-account/payment-details'
    },
    noCardsMessage () {
      let message = false
      if ((!this.isUserExists && this.user.telephone) || (this.isUserExists && !this.getCards.length && this.user.telephone)) {
        message = 'You have no added cards'
      }
      return message
    }
  },
  methods: {
    ...mapActions({
      getUserStatus: 'ipay-payment-user/getUserStatus',
      getCardsList: 'ipay-payment-user/getCardsList',
      updateUser: 'user/update',
      deleteCard: 'ipay-payment-user/deleteCard',
      openAddCardLink: 'ipay-payment-user/openIPayActionLink',
      loadIPayActionLink: 'ipay-payment-user/loadIPayActionLink'
    }),
    sendTelephoneValue () {
      console.log('in sendTelephoneValue() function')

      let payload = Object.assign({}, this.user)
      payload.telephone = this.telephone
      if (!payload.hasOwnProperty('deleted_addresses')) {
        payload.deleted_addresses = []
      }

      this.updateUser({ customer: payload })
    },
    getPaymentData () {
      this.ipayLoading = true
      this.getUserStatus({
        customerId: this.getCustomerId,
        phoneNumber: this.getPhoneNumber(true)
      })
        .then(() => this.loadIPayActionLink({
          customerId: this.getCustomerId,
          phoneNumber: this.getPhoneNumber(true),
          lang: 'ua',
          successUrl: this.successUrl
        }))
        .then(async res => {
          if (this.isUserExists) {
            await this.getCardsList({
              customerId: this.getCustomerId,
              phoneNumber: this.getPhoneNumber(true),
              token: this.getCustomerToken
            })
          }
          return res
        })
        .finally(() => {
          this.ipayLoading = false
        })
    }
  }
}
</script>

<style scoped lang="scss">
.payment {
  display: flex;
  flex-direction: column;

  @media only screen and (max-width: 576px) {
    padding: 0 20px;
  }

  &-line {
    height: 60px;
    margin-bottom: 20px;

    display: flex;
    justify-content: space-between;
    align-items: center;

    @media only screen and (max-width: 768px) {
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }

    @media only screen and (max-width: 576px) {
      height: 0;
      margin-bottom: 0;
    }

    &-title {
      margin: 0;

      color: #111;
      font-size: 20px;
      font-weight: 600;

      @media only screen and (max-width: 576px) {
        display: none;
      }
    }
  }

  &-block {

    &-telephone {
      display: flex;
      flex-direction: column;
      align-content: flex-start;

      &-title {
        padding-left: 24px;

        color: #e02124;
        font-size: 14px;
        font-weight: 400;

        background-image: url('../../../../assets/account/icon-attention.png');
        background-position: left center;
        background-size: 15px;
        background-repeat: no-repeat;
      }

      &-wrapper {
        display: flex;
        align-content: center;

        @media only screen and (max-width: 991px) {
          flex-direction: column;
          align-content: flex-start;
        }
      }

      &-field {
        padding: 0;

        @media only screen and (max-width: 991px) {
          min-height: 3em;
        }
      }

      &-btn {
        width: auto;
        padding: 12px 30px;
        margin-left: 10px;

        align-self: baseline;

        color: #fff;
        font-size: 16px;
        font-weight: 400;

        background-color: #11bb2e;
        border: none;
        border-radius: 5px;

        outline: none;

        @media only screen and (max-width: 991px) {
          min-width: 100%;
          margin: 0;
        }

        &:hover {
          cursor: pointer;
          background-color: rgba(17, 187, 46, 0.8);
        }
      }
    }

    &-list {
      display: flex;
      flex-wrap: wrap;
    }

    &-actions {
      margin-bottom: 20px;

      display: flex;
      justify-content: flex-start;

      @media only screen and (max-width: 576px) {
        margin-top: 20px;
      }

      &-btn {
        width: auto;
        padding: 16px 84px;

        color: #fff;
        font-size: 16px;
        font-weight: 400;

        background-color: #11bb2e;
        border: none;
        border-radius: 5px;

        outline: none;

        &:hover {
          cursor: pointer;
          background-color: rgba(17, 187, 46, 0.8);
        }
      }
    }

    &-nothing {
      width: 100%;
      margin-top: 30px;
      text-align: center;

      &-text {
        color: #111;
        font-size: 18px;
        font-weight: 400;
      }
    }
  }
}
</style>
